import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableRow, Typography } from '@mui/material';
import { TooltipCC } from 'components/common';
import NotFoundData from 'components/NotFound';
import { PaginationButtons, TableHeaderCC } from 'components/Table';
import { Fragment, isValidElement } from 'react';
import { transHeadCells } from 'utils/functions';
import { getIndexCellTable } from 'utils/helpers';

import moment from 'moment';
import SwitchIOS from 'shared/components/base/SwitchIOS';
import { getStatusConfig } from './helper';
import { STATUS_COLOR } from './constant';

/**
 * Reusable table component without row selection.
 *
 * @param {Array} headCells - Column configuration (same shape as SelectingTable)
 * @param {Array} data - Row data to display
 * @param {Number} page - Current page index (0‑based)
 * @param {Number} size - Number of rows per page
 * @param {Number} total - Total number of rows (for pagination)
 * @param {Function} handleChangePage - Callback when page changes
 * @param {Function} renderCustomCells - Optional custom cell renderer. Receives (row, labelId) and should return an array of <TableCell/> elements.
 */
const TableComponent = ({
    headCells = [],
    data = [],
    page = 0,
    size = 10,
    total = 0,
    handleChangePage,
    handleChangeLimit,
    useDynamicLimit = false,
    renderCustomCells,
    renderActionsCell,
    t
}) => {
    const headCellTable = transHeadCells(headCells, t)

    const renderCellContent = (headCell, row) => {
        if (headCell.type === 'status') {
            if (headCell.subType === 'toggle') {
                return (
                    <SwitchIOS
                        checked={row[headCell.id]}
                        onChange={(e) => headCell.onChange?.(e, row)}
                        {...headCell.props}
                    />
                );
            }
            if (headCell.subType === 'text') {
                const status = row[headCell.id]
                if (headCell?.useStatusValue) {
                    const { color, key } = getStatusConfig(status, headCell);

                    return (
                        <Typography align="left" sx={{ fontWeight: 500, color: color, width: headCell?.width || 200 }}>
                            {t(key)}
                        </Typography>
                    )
                }
                return (
                    <Typography align="left" sx={{ fontWeight: 500, color: status ? STATUS_COLOR.active : STATUS_COLOR.inactive, width: headCell?.width || 200 }}>
                        {status ? t(headCell.activeKey) : t(headCell.inActiveKey)}
                    </Typography>
                )
            }
            if (headCell.render) {
                return headCell.render(row);
            }
        }
        if (headCell.type === 'time') {
            const timeValue = row[headCell.id];
            if (!timeValue) return '---';
            const format = headCell.format || 'DD/MM/YYYY HH:mm:ss';
            const isUnix = headCell.isUnix !== false; // defaults to true based on project usage
            return moment(isUnix ? timeValue * 1000 : timeValue).format(format);
        }
        if (headCell.type === 'text') {
            return row[headCell.id] || '---';
        }
        return row[headCell.id];
    };

    const renderCellWithTooltip = (headCell, row) => {
        const content = renderCellContent(headCell, row);

        if (isValidElement(content)) {
            return content;
        }

        const displayContent = content ?? '---';
        const tooltipTitle = String(displayContent);

        return (
            <TooltipCC title={tooltipTitle} placement="bottom-start">
                <Typography
                    component="span"
                    sx={{
                        display: 'block',
                        width: '100%',
                        maxWidth: '100%',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                    }}
                >
                    {displayContent}
                </Typography>
            </TooltipCC>
        );
    };

    return (
        <Box sx={{ width: '100%', flex: 1, display: 'flex', flexDirection: 'column' }}>
            {/* Toolbar can be added here if needed in the future */}
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 0 }}>
                <Table stickyHeader sx={{ minWidth: 750 }} size="small" aria-labelledby="tableTitle">
                    <TableHeaderCC headCellTable={headCellTable} />
                    <TableBody>
                        {data && data.length > 0 ? (
                            data.map((row, index) => {
                                const labelId = `table-cell-${index}`;
                                return (
                                    <TableRow
                                        hover
                                        tabIndex={-1}
                                        key={row.id ?? index}
                                        sx={{ cursor: 'default' }}
                                    >
                                        {/* Index column */}
                                        <TableCell align="center" sx={{ width: 60 }}>
                                            {getIndexCellTable(page, size, index)}
                                        </TableCell>

                                        {renderCustomCells ? (
                                            renderCustomCells(row, labelId)
                                        ) : (
                                            <Fragment>
                                                {
                                                    headCells.map((headCell, cellIndex) => {
                                                        if (cellIndex === 0 || headCell.id === 'actions') return null; // skip first column (usually "id" placeholder)
                                                        return (
                                                            <TableCell
                                                                key={headCell.id}
                                                                align={headCell.numeric ? 'right' : 'left'}
                                                                padding={headCell.disablePadding ? 'none' : 'normal'}
                                                                sx={{ maxWidth: headCell?.width, width: headCell?.width }}
                                                            >
                                                                {renderCellWithTooltip(headCell, row)}
                                                            </TableCell>
                                                        );
                                                    })
                                                }
                                                {renderActionsCell?.(row)}
                                            </Fragment>
                                        )}
                                    </TableRow>
                                );
                            })
                        ) : (
                            <TableRow>
                                <TableCell colSpan={headCells.length + 1} sx={{ height: 'calc(100vh - 280px)', borderBottom: 'none' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                                        <NotFoundData />
                                    </Box>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            {total ? (
                <PaginationButtons page={page} handleChangePage={handleChangePage} handleChangeLimit={handleChangeLimit} useDynamicLimit={useDynamicLimit} limit={size} total={total} />
            ) : null}
        </Box>
    );
};

export default TableComponent;
